import { Fragment, Suspense, lazy, useMemo, useState } from 'react'
import { Navigation } from 'lucide-react'
import { Button } from '../ui'
import { EntityChips } from './EntityChips'
import { useFeed, usePeople, useShowings, useVenues } from '../lib/queries'
import { useProfile } from '../lib/profile'
import { programmeFor, type HallVenue } from '../lib/place'
import { browseQuery, factLine, type Chip } from '../lib/entities'
import { languageAllowed, laneOf, talkLanguageAllowed } from '../lib/entries'
import { closedLine } from '../lib/venueOpen'
import { go } from '../lib/router'
import type { VenueRow } from '../lib/derive'
import './PlaceInfo.css'

// Split out for the same reason `AccountSheet.tsx` splits it: a phone's
// worth of admin editing has no business in the bundle every visitor to a
// place page downloads, and this is the only other caller.
const VenueAdmin = lazy(() => import('./VenueAdmin').then(m => ({ default: m.VenueAdmin })))

/** The host, for a link that has to be checkable rather than pretty — the
 *  same rule `EventSheet.tsx`'s `hostOf` states for a ticket link. */
function hostOf(url: string): string | null {
  try { return new URL(url).host.replace(/^www\./, '') || null } catch { return null }
}

interface Fact { label: string; value: string; href?: string }

/** At most this many artists before the rest go behind "+N more". A building
 *  that has shown thirty people has said what kind of building it is. */
const SHOWN_CAP = 30

/** A chip tap leaves the place page for Browse, filtered to that person —
 *  and the place filter is dropped on the way, because a person is a
 *  different axis: "everything by this artist" is the question the tap asks,
 *  not "everything by this artist here". */
const pickEntity = (chip: Chip) => {
  go('browse', null, browseQuery(chip))
  window.scrollTo({ top: 0 })
}

/**
 * Every artist this building has shown, matched people first.
 *
 * Rule 3 of WP 3.4a, which `PlaceProgramme` states too: **nothing is fetched
 * for this venue.** The three shared queries every screen already holds are
 * windowed by `programmeFor` — the same function, so the Info tab and the
 * programme beside it cannot disagree about which rows play here — and the
 * only request this adds is `usePeople`, one lookup of the ids those rows
 * already carry (`feed_lite.people`, K11).
 *
 * Order: a person Wikidata knows about first, since a chip with a fact line
 * under it is the one worth reading; then by the most recent show they are in,
 * so a gallery's current wall comes before its archive.
 */
function useArtistsShown(venueId: string): Chip[] {
  const feedQ = useFeed()
  const showQ = useShowings()
  const venuesQ = useVenues()
  const { english_only: englishOnly, talks_language: talksLanguage } = useProfile()

  const seen = useMemo(() => {
    if (!feedQ.data || !showQ.data || !venuesQ.data) return new Map<string, string>()
    const venues = new Map<string, HallVenue>(
      venuesQ.data.list.map(v => [v.id, { id: v.id, name: v.name, parent_id: v.parent_id }]))
    // The same rows the Programme tab beside this one lists — `PlaceProgramme`
    // filters the shared feed this way before calling `programmeFor`, and a
    // page whose Info tab named an artist its Programme tab does not show
    // would be one screen disagreeing with itself.
    const rows = feedQ.data.filter(e =>
      languageAllowed(e, englishOnly) && talkLanguageAllowed(e, talksLanguage) && !laneOf(e).hidden && e.type !== 'news')
    const out = new Map<string, string>()
    for (const { row } of programmeFor(venueId, rows, showQ.data.rows, venues)) {
      const at = row.run_start ?? row.published_on ?? ''
      // `?? []`: a row cached before E1 has no `people` (see Browse.tsx).
      for (const p of row.people ?? []) {
        if (p.role !== 'artist') continue
        const best = out.get(p.id)
        if (best === undefined || at > best) out.set(p.id, at)
      }
    }
    return out
  }, [feedQ.data, showQ.data, venuesQ.data, venueId, englishOnly, talksLanguage])

  const ids = useMemo(() => [...seen.keys()], [seen])
  const people = usePeople(ids).data

  return useMemo(() => {
    if (!people) return []
    const byId = new Map(people.map(p => [p.id, p]))
    return [...seen.keys()]
      .map(id => byId.get(id))
      .filter((p): p is NonNullable<typeof p> => !!p)
      .sort((a, b) => {
        const m = Number(b.reconcile_status === 'matched') - Number(a.reconcile_status === 'matched')
        if (m) return m
        const at = (seen.get(b.id) ?? '').localeCompare(seen.get(a.id) ?? '')
        return at || a.name.localeCompare(b.name)
      })
      .slice(0, SHOWN_CAP)
      .map((p): Chip => ({
        kind: 'person', id: p.id, name: p.name, role: 'artist',
        fact: factLine(p), href: p.wikipedia_url ?? null,
      }))
  }, [people, seen])
}

/**
 * P2: what a place is, rather than what is on there — address, the website,
 * the halls under this building, and the photo's credit.
 *
 * Two rows P2 asks for are never drawn: a walk/transit duration, because
 * `reach.ts` has no such function to read one from, and opening hours,
 * because no `venue_hours` table exists yet (`database.types.ts` has only
 * `venues.hours_src`, a free-text scrape rather than structured hours — the
 * IA plan's step 2). Both are omitted rather than guessed at.
 */
export function PlaceInfo({ venue, halls, canEdit }: { venue: VenueRow; halls: VenueRow[]; canEdit: boolean }) {
  const [editing, setEditing] = useState(false)
  const artists = useArtistsShown(venue.id)

  const cityLine = [venue.postcode, venue.district].filter(Boolean).join(' ')
  const address = [venue.street, cityLine || null].filter(Boolean).join(', ') || null
  const mapsHref = venue.lat != null && venue.lng != null
    ? `https://maps.google.com/?q=${venue.lat},${venue.lng}`
    : address
      ? `https://maps.google.com/?q=${encodeURIComponent([venue.name, address].join(', '))}`
      : null

  const facts: Fact[] = []
  if (address) facts.push({ label: 'Address', value: address })
  if (venue.site_url) {
    facts.push({ label: 'Website', value: hostOf(venue.site_url) ?? venue.site_url, href: venue.site_url })
  }

  // The stages under this building — the Philharmonie's Großer Saal and its
  // Foyer, each its own `venues` row with this one as `parent_id`. A fact row
  // like the others rather than a browser-bullet `<ul>` (the only one in the
  // app). Joined with a middle dot, not a comma: the names carry their own
  // ("Philharmonie Berlin, Foyer").
  if (halls.length > 0) facts.push({ label: 'Halls', value: halls.map(h => h.name).join(' · ') })

  const hasCredit = !!venue.hero_key && !!venue.hero_license && venue.hero_license !== 'unknown'

  // `venues.closed_at`: the place is hidden from the index and an old saved
  // entry is the only way here, so the page says why there is nothing on.
  // One quiet line, above the address that may no longer be worth walking to.
  const closed = closedLine(venue)

  return (
    <div>
      {closed && (
        <p className="note">{closed}. Kept for the events that were here.</p>
      )}
      {facts.length > 0 && (
        <dl className="pd__facts">
          {facts.map(f => (
            <Fragment key={f.label}>
              <dt>{f.label}</dt>
              <dd>
                {f.href
                  ? <a href={f.href} target="_blank" rel="noopener noreferrer">{f.value}</a>
                  : f.value}
                {f.label === 'Address' && mapsHref && (
                  <a className="pd__dir" href={mapsHref} target="_blank" rel="noopener noreferrer">
                    <Navigation size={14} /> Directions
                  </a>
                )}
              </dd>
            </Fragment>
          ))}
        </dl>
      )}

      {/* K9: who this building has shown, across everything on its programme —
          the gallery's own list of artists, which is the second half of the
          enrichment Doug asked for (the first is the fact line on the sheet).
          Nothing at all where there are no artist edges: a concert hall gets
          no empty heading. */}
      {artists.length > 0 && (
        <div className="pd__shown">
          <p className="pd__shown-head">Artists shown</p>
          <EntityChips chips={artists} onPick={pickEntity} />
        </div>
      )}

      {/* The credit, where the picture came from Commons — the condition of
          showing the photograph at all, not a footnote about it (same rule
          `Hero`/`FilmPoster` state). Moved here from under the hero itself:
          rule 1 says the venue photo appears once, and its credit belongs
          with the rest of what this building is, not repeated on every tab. */}
      {hasCredit && (
        <p className="credit">
          Photo{venue.hero_credit ? ` by ${venue.hero_credit}` : ''} · {venue.hero_license}
          {venue.hero_source_url && (
            <> · <a href={venue.hero_source_url} target="_blank" rel="noopener noreferrer">source</a></>
          )}
        </p>
      )}

      {canEdit && (
        <div className="pd__edit">
          <Button variant="quiet" size="sm" onClick={() => setEditing(true)}>Edit</Button>
        </div>
      )}

      {editing && (
        <Suspense fallback={null}>
          <VenueAdmin initialVenueId={venue.id} onClose={() => setEditing(false)} />
        </Suspense>
      )}
    </div>
  )
}
